import React, { useState, useEffect } from 'react';
import Spinner from '../components/Spinner';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../services/api';
import { useNotification } from '../components/Notification';
import { getCurrencySymbol } from '../utils/currency';
import './Repairs.css';
import './DetailPages.css';

function RepairDetail({ tenantId }) {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const { id } = useParams();
  const [repair, setRepair] = useState(null);
  const [car, setCar] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [invoice, setInvoice] = useState(null);
  const [hourlyRates, setHourlyRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tenant, setTenant] = useState(null);

  useEffect(() => {
    if (tenantId && id) {
      loadData();
    }
  }, [tenantId, id]);

  const loadData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadTenant(),
        loadRepair()
      ]);
    } finally {
      setLoading(false);
    }
  };

  const loadTenant = async () => {
    try {
      const data = await api.get('/tenants', null);
      const currentTenant = data.find(t => t.id === tenantId) || data[0];
      setTenant(currentTenant);
    } catch (error) {
      console.error('Error loading tenant:', error);
    }
  };

  const currency = getCurrencySymbol(tenant?.taxSystem || 'DE');

  const loadRepair = async () => {
    try {
      const data = await api.get(`/repairs/${id}`, tenantId);
      setRepair(data);
      
      if (data.carId) {
        loadCar(data.carId);
      }
      if (data.invoiceId) {
        loadInvoice(data.invoiceId);
      }
      await loadHourlyRates();
    } catch (error) {
      console.error('Error loading repair:', error);
      showNotification('Fehler beim Laden der Reparatur', 'error');
      navigate('/repairs');
    }
  };

  const loadCar = async (carId) => {
    try {
      const data = await api.get(`/cars/${carId}`, tenantId);
      setCar(data);
      if (data.customerId) {
        loadCustomer(data.customerId);
      }
    } catch (error) {
      console.error('Error loading car:', error);
    }
  };

  const loadCustomer = async (customerId) => {
    try {
      const data = await api.get(`/customers/${customerId}`, tenantId);
      setCustomer(data);
    } catch (error) {
      console.error('Error loading customer:', error);
    }
  };

  const loadInvoice = async (invoiceId) => {
    try {
      const data = await api.get(`/invoices/${invoiceId}`, tenantId);
      setInvoice(data);
    } catch (error) {
      console.error('Error loading invoice:', error);
    }
  };

  const loadHourlyRates = async () => {
    try {
      const data = await api.get('/hourly-rates', tenantId);
      setHourlyRates(data);
    } catch (error) {
      console.error('Error loading hourly rates:', error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: currency === 'CHF' ? 'CHF' : 'EUR',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'Ausstehend',
      in_progress: 'In Bearbeitung',
      completed: 'Abgeschlossen'
    };
    return labels[status] || status;
  };

  const getHourlyRate = (hourlyRateId) => {
    const rate = hourlyRates.find(r => r.id === hourlyRateId);
    return rate ? rate.rate : 0;
  };

  if (loading) {
    return <Spinner />;
  }

  if (!repair) {
    return <div>Reparatur nicht gefunden</div>;
  }

  const materials = repair.materials || [];
  const labor = repair.labor || [];
  const totalMaterialsAmount = materials.reduce((sum, item) => sum + (item.amount || 0) * (item.quantity || 1), 0);
  let totalLaborAmount = 0;
  for (const lab of labor) {
    if (lab.hours && lab.hourlyRateId) {
      totalLaborAmount += (lab.hours || 0) * getHourlyRate(lab.hourlyRateId);
    }
  }

  return (
    <div className="repair-detail">
      <div className="detail-header">
        <button type="button" onClick={() => navigate('/repairs')} className="btn-secondary">
          ← Zurück
        </button>
        <h1>Reparatur: {repair.description || 'Keine Beschreibung'}</h1>
        <button type="button" onClick={() => navigate(`/repairs/${id}/edit`)} className="btn-primary">
          Bearbeiten
        </button>
      </div>

      <div className="detail-section">
        <h2>Allgemeine Informationen</h2>
        <p><strong>Status:</strong> {getStatusLabel(repair.status)}</p>
        <p><strong>Datum:</strong> {formatDate(repair.date)}</p>
        {repair.mileage && <p><strong>Kilometerstand:</strong> {repair.mileage.toLocaleString('de-DE')} km</p>}
        <p><strong>Gesamtbetrag:</strong> {formatCurrency(repair.totalAmount || 0)}</p>
      </div>

      <div className="detail-section">
        <h2>Fahrzeug</h2>
        {car ? (
          <p>
            <span 
              className="link-button"
              onClick={() => navigate(`/cars/${car.id}`)}
            >
              {car.make} {car.model} {car.year} ({car.licensePlate || 'Kein Kennzeichen'})
            </span>
          </p>
        ) : (
          <p>-</p>
        )}
      </div>

      {customer && (
        <div className="detail-section">
          <h2>Kunde</h2>
          <p>
            <span 
              className="link-button"
              onClick={() => navigate(`/customers/${customer.id}`)}
            >
              {customer.firstName} {customer.lastName}
            </span>
          </p>
        </div>
      )}

      {invoice && (
        <div className="detail-section">
          <h2>Rechnung</h2>
          <p>
            <span 
              className="link-button"
              onClick={() => navigate(`/invoices/${invoice.id}`)}
            >
              {invoice.invoiceNumber} - {formatCurrency(invoice.totalAmount || 0)}
            </span>
          </p>
        </div>
      )}

      {materials.length > 0 && (
        <div className="detail-section">
          <h2>Materialien</h2>
          <table className="data-table">
            <thead>
              <tr>
                <th>Beschreibung</th>
                <th>Menge</th>
                <th>Einzelpreis</th>
                <th>Gesamt</th>
              </tr>
            </thead>
            <tbody>
              {materials.map((item, idx) => (
                <tr key={idx}>
                  <td>{item.description || '-'}</td>
                  <td>{item.quantity || 1}</td>
                  <td>{formatCurrency(item.amount || 0)}</td>
                  <td>{formatCurrency((item.amount || 0) * (item.quantity || 1))}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan="3"><strong>Zwischensumme Materialien:</strong></td>
                <td><strong>{formatCurrency(totalMaterialsAmount)}</strong></td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {labor.length > 0 && (
        <div className="detail-section">
          <h2>Arbeitseinsatz</h2>
          <table className="data-table">
            <thead>
              <tr>
                <th>Beschreibung</th>
                <th>Stunden</th>
                <th>Stundensatz</th>
                <th>Gesamt</th>
              </tr>
            </thead>
            <tbody>
              {labor.map((lab, idx) => {
                const rate = getHourlyRate(lab.hourlyRateId);
                const amount = (lab.hours || 0) * rate;
                return (
                  <tr key={idx}>
                    <td>
                      {lab.description || '-'}
                      {(lab.date || lab.mileage || lab.documentation) && (
                        <div style={{ fontSize: '0.85em', color: '#666', marginTop: '4px' }}>
                          {lab.date && <span>Datum: {formatDate(lab.date)}</span>}
                          {lab.mileage && <span> | Kilometerstand: {lab.mileage.toLocaleString('de-DE')} km</span>}
                          {lab.documentation && (
                            <div style={{ marginTop: '4px' }}>{lab.documentation}</div>
                          )}
                        </div>
                      )}
                    </td>
                    <td>{lab.hours || 0}</td>
                    <td>{formatCurrency(rate)}/h</td>
                    <td>{formatCurrency(amount)}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan="3"><strong>Zwischensumme Arbeitseinsatz:</strong></td>
                <td><strong>{formatCurrency(totalLaborAmount)}</strong></td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}

export default RepairDetail;

